<template>
  <div
    :class="print.classes"
    role="status"
  >
    <template v-if="isCompactSuccess">
      <div class="toast-success-icon">
        <i
          class="bi bi-check-circle-fill"
          aria-hidden="true"
        />
      </div>
      <div class="toast-success-text">{{ message.message }}</div>
      <button
        type="button"
        class="toast-success-close"
        :aria-label="t('close')"
        @click="$emit('close')"
      >
        <i
          class="bi bi-x"
          aria-hidden="true"
        />
      </button>
    </template>
    <template v-else>
      <div class="toast-header">
        <strong class="me-auto">
          {{ print.header }}
          <span
            v-if="message.count > 1"
            class="badge text-bg-dark"
          >{{ message.count }}</span>
        </strong>
        <button
          type="button"
          class="btn-close"
          @click="$emit('close')"
        />
      </div>
      <div class="toast-body">
        <i18n-t
          v-if="message.messageKey"
          :keypath="message.messageKey"
          tag="span"
        >
          <template #emphasis><strong>{{ message.emphasis }}</strong></template>
        </i18n-t>
        <template v-else>
          {{ message.message }}
        </template>
        <div
          v-if="message.reason"
          class="mt-2"
        >{{ t('reason', { reason: message.reason }) }}</div>
      </div>
      <div
        v-if="!permanent"
        class="toast-footer"
      >
        <div class="progress">
          <div
            class="progress-bar"
            :style="{ width: progress }"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.progress {
  height: 0.25rem;
}
.toast-success-icon {
  width: 34px;
  height: 34px;
  border-radius: 11px;
  background: rgba(48, 209, 88, 0.15);
  color: #2e7d32;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  line-height: 1;
  margin-left: 2px;
}
.toast-success-text {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #001f63;
  line-height: 1.3;
  display: flex;
  align-items: center;
}
.toast-success-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #7a8e98;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
}
.toast-success-close:hover {
  background: rgba(229, 57, 53, 0.08);
  color: #c62828;
}
</style>

<script setup>
import { computed, onMounted, onUnmounted, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const styling = computed(() => ({
  info: {
    header: t('toastHeaderInfo'),
    color: 'primary',
  },
  success: {
    header: t('toastHeaderSuccess'),
    color: 'success',
  },
  error: {
    header: t('common.error'),
    color: 'danger',
  },
  warning: {
    header: t('toastHeaderWarning'),
    color: 'warning',
  },
}))

const destroyTimeout = 5000
const progressTickInterval = 100

let destroyTimeoutId = null
let progressIntervalId = null

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  permanent: {
    type: Boolean,
    default: false,
  },
  timeout: {
    type: Number,
    default: 5000,
  },
})
const emit = defineEmits(['close'])
const progressTick = shallowRef(0)

const isCompactSuccess = computed(() => (props.message.type || 'info') === 'success')

const print = computed(() => {
  const type = props.message.type || 'info'
  const s = styling.value[type] || styling.value.info
  return {
    header: s.header,
    color: s.color,
    classes: {
      toast: 1,
      show: 1,
      [`toast-type-${type}`]: 1,
      'toast--success-compact': type === 'success',
    },
  }
})

const progress = computed(() => `${(progressTick.value / destroyTimeout) * 100}%`)

onMounted(() => {
  if (props.permanent) {
    return
  }
  destroyTimeoutId = setTimeout(() => emit('close'), props.timeout)
  progressIntervalId = setInterval(() => {
    progressTick.value += progressTickInterval
  }, progressTickInterval)
})

onUnmounted(() => {
  clearTimeout(destroyTimeoutId)
  clearInterval(progressIntervalId)
  destroyTimeoutId = null
  progressIntervalId = null
})
</script>

<i18n>
{
  "ru": {
    "toastHeaderInfo": "Уведомление",
    "toastHeaderWarning": "Внимание",
    "toastHeaderSuccess": "Готово",
    "reason": "Причина: {reason}",
    "close": "Закрыть"
  },
  "en": {
    "toastHeaderInfo": "Notification",
    "toastHeaderWarning": "Warning",
    "toastHeaderSuccess": "Done",
    "reason": "Reason: {reason}",
    "close": "Close"
  }
}
</i18n>
